import type { AxiosRequestConfig } from 'axios'
import { httpClient } from '@/configs/axios.config'
import type { ScorecardKpiStatus } from '@/types/api'

const scorecardReadConfig: AxiosRequestConfig = {
  skipNotFoundRedirect: true,
}

export type ScorecardPeriodType =
  | 'LTM'
  | 'TTM'
  | 'YTD'
  | 'QUARTERLY'
  | 'ANNUAL'
  | 'MONTHLY'
  | 'AS_OF'

export type ScorecardMetricDataType =
  | 'CURRENCY'
  | 'PERCENTAGE'
  | 'NUMBER'
  | 'RATIO'

export type ScorecardMetricCategory =
  | 'INCOME_STATEMENT'
  | 'BALANCE_SHEET'
  | 'CASH_FLOW'
  | 'KPI'

export type { ScorecardKpiStatus }

export type ScorecardKpiDirection = 'HIGHER_IS_BETTER' | 'LOWER_IS_BETTER'

export type ScorecardTargetSource = 'MANUAL'

export type ScorecardStatusSource = 'MANUAL' | 'CALCULATED'

export type ScorecardTargetMode = 'ABSOLUTE' | 'PERCENT_CHANGE'

export type ScorecardMetricTypeDto = {
  id: string
  code: string
  name: string
  category: ScorecardMetricCategory
  dataType: ScorecardMetricDataType
}

export type ScorecardMetricLabelDto = {
  id: string
  labeledAs: string
  globalSectionName?: string | null
  dataType?: ScorecardMetricDataType | null
  metricType?: ScorecardMetricTypeDto | null
}

export type ScorecardActualDto = {
  value: number
  periodType: ScorecardPeriodType
  periodStartDate: string
  periodEndDate: string
  asOfDate?: string | null
  lastSyncedAt?: string | null
  source: 'MANUAL' | 'CHRONOGRAPH'
}

export type ScorecardProgressDto = {
  percent: number | null
  delta: number
  favorable: boolean
  startValue: number
  currentValue: number
  targetValue: number
  progressValue: number
  goalSpan: number
  remainingValue: number
}

export type ScorecardKpiCheckpointDto = {
  checkpointDate: string
  value: number
  label?: string | null
}

export type ScorecardKpiDto = {
  planId?: string | null
  configurationEditable?: boolean
  actualSource?: 'MANUAL' | 'CHRONOGRAPH'
  /** Set when the KPI was published from OGSM; it is edited only there. */
  ogsmNodeId?: string | null
  id: string
  companyId: string
  metricLabelId: string
  periodType: ScorecardPeriodType
  targetSource: ScorecardTargetSource
  targetValue: number
  targetMode: ScorecardTargetMode
  targetPercent: number | null
  goalChangePercent?: number | null
  calculatedStatus: ScorecardKpiStatus
  startValue: number | null
  startDate: string
  targetDate: string
  statusSource: ScorecardStatusSource
  status: ScorecardKpiStatus
  direction: ScorecardKpiDirection
  displayName?: string | null
  unitLabel?: string | null
  sortOrder: number
  notes?: string | null
  isActive: boolean
  deletedAt?: string | null
  metricLabel: ScorecardMetricLabelDto
  actual: ScorecardActualDto | null
  progress: ScorecardProgressDto | null
  actualEditable: boolean
  checkpoints: ScorecardKpiCheckpointDto[]
  createdAt: string
  updatedAt: string
}

export type ScorecardTrackedKpiDto = {
  id: string
  companyId: string
  metricLabelId: string
  displayName?: string | null
  sortOrder: number
  isActive: boolean
  metricLabel: ScorecardMetricLabelDto
  actual: ScorecardActualDto | null
  availablePeriods: ScorecardPeriodType[]
  createdAt: string
  updatedAt: string
}

export type ScorecardResponseDto = {
  companyId: string
  kpis: ScorecardKpiDto[]
  trackedKpis: ScorecardTrackedKpiDto[]
}

export type PortalScorecardResponseDto = {
  companyId: string
  kpis: ScorecardKpiDto[]
}

export type ScorecardMetricOptionDto = {
  metricLabelId: string
  labeledAs: string
  globalSectionName?: string | null
  dataType?: ScorecardMetricDataType | null
  metricType?: ScorecardMetricTypeDto | null
  sortOrder?: number | null
  actual?: ScorecardActualDto | null
}

export type ScorecardMetricOptionsResponseDto = {
  companyId: string
  periodType: ScorecardPeriodType
  options: ScorecardMetricOptionDto[]
}

export type CreateScorecardKpiInput = {
  metricLabelId?: string
  metricName?: string
  dataType?: ScorecardMetricDataType
  periodType: ScorecardPeriodType
  targetValue?: number
  targetMode?: ScorecardTargetMode
  targetPercent?: number
  statusSource?: ScorecardStatusSource
  startValue: number
  startDate: string
  targetDate: string
  status?: ScorecardKpiStatus
  direction: ScorecardKpiDirection
  displayName?: string
  unitLabel?: string
  sortOrder?: number
  notes?: string
  checkpoints?: ScorecardKpiCheckpointDto[]
}

export type UpdateScorecardKpiInput = Partial<
  Omit<CreateScorecardKpiInput, 'metricLabelId' | 'metricName' | 'dataType'>
>

export type CreateScorecardActualInput = {
  value: number
  asOfDate: string
}

export async function getCompanyScorecard(
  companyId: string
): Promise<ScorecardResponseDto> {
  const response = await httpClient.get<ScorecardResponseDto>(
    `/companies/${companyId}/scorecard`,
    scorecardReadConfig
  )
  return response.data
}

export async function getPortalScorecard(): Promise<PortalScorecardResponseDto> {
  const response = await httpClient.get<PortalScorecardResponseDto>(
    '/portal/company/scorecard',
    scorecardReadConfig
  )
  return response.data
}

export async function getScorecardMetricOptions(
  companyId: string,
  periodType: ScorecardPeriodType,
  includeMetricLabelId?: string
): Promise<ScorecardMetricOptionsResponseDto> {
  const response = await httpClient.get<ScorecardMetricOptionsResponseDto>(
    `/companies/${companyId}/scorecard/metric-options`,
    {
      ...scorecardReadConfig,
      params: { periodType, includeMetricLabelId },
    }
  )
  return response.data
}

export async function createScorecardKpi(
  companyId: string,
  data: CreateScorecardKpiInput
): Promise<ScorecardKpiDto> {
  const response = await httpClient.post<ScorecardKpiDto>(
    `/companies/${companyId}/scorecard/kpis`,
    data
  )
  return response.data
}

export async function updateScorecardKpi(
  companyId: string,
  kpiId: string,
  data: UpdateScorecardKpiInput
): Promise<ScorecardKpiDto> {
  const response = await httpClient.patch<ScorecardKpiDto>(
    `/companies/${companyId}/scorecard/kpis/${kpiId}`,
    data
  )
  return response.data
}

export async function deleteScorecardKpi(
  companyId: string,
  kpiId: string
): Promise<void> {
  await httpClient.delete(`/companies/${companyId}/scorecard/kpis/${kpiId}`)
}

export async function createScorecardActual(
  companyId: string,
  kpiId: string,
  data: CreateScorecardActualInput
): Promise<ScorecardActualDto> {
  const response = await httpClient.post<ScorecardActualDto>(
    `/companies/${companyId}/scorecard/kpis/${kpiId}/actuals`,
    data
  )
  return response.data
}
